/**
 * Tracks /products/{handle} visits in localStorage and renders a Dawn-style product grid
 * (same classes as related-products / featured-collection).
 */
(function () {
  const STORAGE_KEY = 'dawn-recently-viewed-handles';
  const MAX_STORED = 32;

  function readHandles() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string' && h.trim()) : [];
    } catch {
      return [];
    }
  }

  function writeHandles(handles) {
    const unique = [];
    handles.forEach((h) => {
      if (h && !unique.includes(h)) unique.push(h);
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(unique.slice(0, MAX_STORED)));
  }

  function trackCurrentProduct() {
    if (window.__dawnRecentlyViewedTracked) return;
    window.__dawnRecentlyViewedTracked = true;
    const path = (window.location.pathname || '').replace(/\/+$/, '') || '/';
    const m = path.match(/\/products\/([^/]+)$/i);
    if (!m) return;
    const handle = decodeURIComponent(m[1]);
    if (!handle) return;
    const list = readHandles().filter((h) => h !== handle);
    list.unshift(handle);
    writeHandles(list);
  }

  trackCurrentProduct();

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function withWidth(url, w) {
    if (!url) return '';
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}width=${w}`;
  }

  function priceCents(variant) {
    if (!variant || variant.price == null) return 0;
    const p = variant.price;
    if (typeof p === 'number' && Number.isFinite(p)) return p;
    const n = parseFloat(String(p).replace(/,/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  }

  function compareCents(variant) {
    if (!variant || variant.compare_at_price == null) return null;
    const p = variant.compare_at_price;
    if (typeof p === 'number' && Number.isFinite(p)) return p;
    const n = parseFloat(String(p).replace(/,/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  }

  function formatMoney(cents, cfg) {
    const amount = Number(cents) / 100;
    const currency = cfg.currency || 'USD';
    let formatted;
    try {
      formatted = new Intl.NumberFormat(cfg.locale || undefined, {
        style: 'currency',
        currency,
      }).format(amount);
    } catch {
      formatted = `${amount.toFixed(2)} ${currency}`;
    }
    if (cfg.currencyCodeEnabled) {
      formatted = `${formatted} ${currency}`;
    }
    return formatted;
  }

  /** Ajax /products/{handle}.js lists options as { name, position } without values — derive from variants. */
  function optionsWithValues(product) {
    const raw = product.options;
    if (!raw || !raw.length) return [];
    return raw
      .map((o, i) => {
        const pos = typeof o.position === 'number' ? o.position : i + 1;
        const optionIndex = Math.max(0, pos - 1);
        const key = optionIndex === 0 ? 'option1' : optionIndex === 1 ? 'option2' : 'option3';
        const set = new Set();
        (product.variants || []).forEach((v) => {
          const val = v[key];
          if (val != null && val !== '') set.add(val);
        });
        const name = typeof o === 'string' ? o : o.name || `Option ${pos}`;
        return { name, values: Array.from(set), optionIndex };
      })
      .filter((x) => x.values.length > 0);
  }

  function pickSwatchOption(product) {
    const list = optionsWithValues(product);
    if (!list.length) return null;
    let idx = list.findIndex((o) => {
      const n = (o.name || '').toLowerCase();
      return n.includes('color') || n.includes('colour');
    });
    if (idx < 0) idx = 0;
    return list[idx];
  }

  function findVariantForOptionValue(product, optionIndex, value) {
    const key = optionIndex === 0 ? 'option1' : optionIndex === 1 ? 'option2' : 'option3';
    return (product.variants || []).find((v) => v[key] === value) || null;
  }

  function variantFeaturedSrc(variant) {
    if (!variant || !variant.featured_image) return '';
    const f = variant.featured_image;
    if (typeof f === 'string') return f.startsWith('//') ? `https:${f}` : f;
    if (f.src) return f.src.startsWith('//') ? `https:${f.src}` : f.src;
    return '';
  }

  function buildCompactColorBlock(product, cfg, productUrl) {
    if (!cfg.compactProductCards || product.has_only_default_variant) return '';
    const swatch = pickSwatchOption(product);
    if (!swatch || !swatch.values.length) return '';
    const n = swatch.values.length;
    const countLabel = n === 1 ? '1 color' : `${n} colors`;
    const baseUrl = productUrl.split('?')[0];
    const limit = 5;
    const values = swatch.values.slice(0, limit);
    let items = '';
    for (const value of values) {
      const swatchVariant = findVariantForOptionValue(product, swatch.optionIndex, value);
      if (!swatchVariant) continue;
      const vUrl = `${baseUrl}?variant=${swatchVariant.id}`;
      const title = escapeHtml(value);
      const imgSrc = variantFeaturedSrc(swatchVariant);
      if (imgSrc) {
        const src = escapeHtml(withWidth(imgSrc, 120));
        items += `<a role="listitem" href="${escapeHtml(vUrl)}" class="card-color-swatches__item" title="${title}"><img class="card-color-swatches__img" src="${src}" alt="${title}" loading="lazy" width="40" height="40"></a>`;
      } else {
        items += `<a role="listitem" href="${escapeHtml(vUrl)}" class="card-color-swatches__item" title="${title}"><span class="card-color-swatches__fallback" aria-hidden="true"></span></a>`;
      }
    }
    const more = n > 5 ? n - 5 : 0;
    const moreHtml =
      more > 0 ? `<span class="card-color-swatches__more caption" aria-hidden="true">+ ${more} more</span>` : '';
    return `<p class="card-color-count caption">${escapeHtml(countLabel)}</p><div class="card-color-swatches" role="list">${items}${moreHtml}</div>`;
  }

  function featuredImageUrl(product) {
    const fi = product.featured_image;
    if (typeof fi === 'string' && fi) return fi.startsWith('//') ? `https:${fi}` : fi;
    if (fi && typeof fi === 'object' && fi.src) return fi.src.startsWith('//') ? `https:${fi.src}` : fi.src;
    const img0 = product.images && product.images[0];
    if (typeof img0 === 'string') return img0.startsWith('//') ? `https:${img0}` : img0;
    if (img0 && img0.src) return img0.src.startsWith('//') ? `https:${img0.src}` : img0.src;
    const v0 = product.variants && product.variants[0];
    if (v0 && v0.featured_image) {
      const f = v0.featured_image;
      if (typeof f === 'string') return f.startsWith('//') ? `https:${f}` : f;
      if (f.src) return f.src.startsWith('//') ? `https:${f.src}` : f.src;
    }
    return '';
  }

  function imageAlt(product) {
    const fi = product.featured_image;
    if (fi && typeof fi === 'object' && fi.alt) return fi.alt;
    const img0 = product.images && product.images[0];
    if (img0 && typeof img0 === 'object' && img0.alt) return img0.alt;
    return product.title || '';
  }

  function aspectRatio(product) {
    const fi = product.featured_image;
    if (fi && typeof fi === 'object' && fi.aspect_ratio) return Number(fi.aspect_ratio) || 1;
    const img0 = product.images && product.images[0];
    if (img0 && typeof img0 === 'object' && img0.aspect_ratio) return Number(img0.aspect_ratio) || 1;
    return 1;
  }

  function firstVariant(product) {
    if (!product.variants || !product.variants.length) return null;
    const avail = product.variants.find((v) => v.available);
    return avail || product.variants[0];
  }

  function buildPriceHtml(product, cfg) {
    const v = firstVariant(product);
    const fmt = (c) => formatMoney(c, cfg);
    if (!v) {
      return '<div class="price"><div class="price__container"><div class="price__regular"><span class="price-item price-item--regular"></span></div><div class="price__sale"></div></div></div>';
    }
    const price = priceCents(v);
    const compare = compareCents(v);
    const onSale = compare != null && compare > price && product.available !== false;
    let inner = '<div class="price__container">';
    if (onSale) {
      inner +=
        '<div class="price__regular"><span class="visually-hidden visually-hidden--inline">Regular price</span><span><s class="price-item price-item--regular">' +
        escapeHtml(fmt(compare)) +
        '</s></span></div>';
      inner +=
        '<div class="price__sale"><span class="visually-hidden visually-hidden--inline">Sale price</span><span class="price-item price-item--sale price-item--last">' +
        escapeHtml(fmt(price)) +
        '</span></div>';
    } else {
      inner +=
        '<div class="price__regular"><span class="visually-hidden visually-hidden--inline">Regular price</span><span class="price-item price-item--regular">' +
        escapeHtml(fmt(price)) +
        '</span></div>';
      inner += '<div class="price__sale"></div>';
    }
    inner += '</div>';
    const soldOut = product.available === false;
    return `<div class="price${soldOut ? ' price--sold-out' : ''}${onSale ? ' price--on-sale' : ''}">${inner}</div>`;
  }

  function buildCardHtml(product, cfg) {
    const v = firstVariant(product);
    const productUrl = cfg.rootUrl.replace(/\/$/, '') + (product.url || `/products/${product.handle}`);
    const title = escapeHtml(product.title);
    const sid = cfg.sectionId;
    const pid = product.id;
    const cardStyle = cfg.cardStyle;
    const cardColor = cfg.cardColorScheme;
    const soldScheme = cfg.soldOutBadgeColorScheme;
    const saleScheme = cfg.saleBadgeColorScheme;
    const badgePos = cfg.badgePosition || 'badge--bottom-left';

    const naturalRatio = aspectRatio(product);
    const effRatio =
      cfg.mediaAspectRatio === 'portrait' ? 0.8 : cfg.mediaAspectRatio === 'square' ? 1 : naturalRatio || 1;
    const effPercent = (1 / effRatio) * 100;

    const imgUrl = featuredImageUrl(product);
    const alt = escapeHtml(imageAlt(product) || product.title);

    const innerGradient = cardStyle === 'standard' ? ` color-${cardColor} gradient` : '';
    const cardGradient = cardStyle === 'card' ? ` color-${cardColor} gradient` : '';
    const shapeClass =
      cfg.imageShape && cfg.imageShape !== 'default' ? ` shape--${cfg.imageShape} color-${cardColor} gradient` : '';

    let badgeInner = '';
    if (product.available === false) {
      badgeInner = `<span id="NoMediaStandardBadge-${sid}-${pid}" class="badge badge--bottom-left color-${soldScheme}">${escapeHtml(cfg.strings.soldOut)}</span>`;
    } else if (v && compareCents(v) != null && compareCents(v) > priceCents(v)) {
      badgeInner = `<span id="NoMediaStandardBadge-${sid}-${pid}" class="badge badge--bottom-left color-${saleScheme}">${escapeHtml(cfg.strings.onSale)}</span>`;
    }

    const vendorHtml =
      cfg.showVendor && product.vendor
        ? `<span class="visually-hidden">${escapeHtml(cfg.strings.vendor)}</span><div class="caption-with-letter-spacing light">${escapeHtml(product.vendor)}</div>`
        : '';

    const sizesAttr = escapeHtml(
      `(min-width: ${cfg.pageWidth}px) ${Math.max(100, Math.floor((cfg.pageWidth - 130) / cfg.columnsDesktop))}px, (min-width: 990px) calc((100vw - 130px) / ${cfg.columnsDesktop}), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)`
    );

    const mediaBlock = imgUrl
      ? `<div class="card__media${shapeClass}">
          <div class="media media--transparent media--hover-effect">
            <img
              srcset="${escapeHtml(withWidth(imgUrl, 165))} 165w, ${escapeHtml(withWidth(imgUrl, 360))} 360w, ${escapeHtml(withWidth(imgUrl, 533))} 533w, ${escapeHtml(withWidth(imgUrl, 720))} 720w"
              src="${escapeHtml(withWidth(imgUrl, 533))}"
              sizes="${sizesAttr}"
              alt="${alt}"
              class="motion-reduce"
              loading="lazy"
              width="533"
              height="${Math.round(533 / effRatio)}"
            >
          </div>
        </div>`
      : '';

    const cardMediaType = imgUrl ? 'card--media' : 'card--text';
    const cardRatioClass = !imgUrl && cardStyle === 'card' ? ' ratio' : '';

    const cardShapeClass = cfg.imageShape && cfg.imageShape !== 'default' ? ' card--shape' : '';

    const firstTitleIdAttr = !imgUrl && cardStyle === 'standard' ? ` id="title-${sid}-${pid}"` : '';
    const secondTitleIdAttr = imgUrl || cardStyle === 'card' ? ` id="title-${sid}-${pid}"` : '';

    const compact = !!cfg.compactProductCards;
    const wrapperCompact = compact ? ' product-card-wrapper--compact-hover' : '';
    const cardCompact = compact ? ' card--compact-hover' : '';

    const colorBlock = buildCompactColorBlock(product, cfg, productUrl);

    return `<li class="grid__item">
  <div class="card-wrapper product-card-wrapper underline-links-hover${wrapperCompact}">
    <div class="card card--${cardStyle} ${cardMediaType}${cardGradient}${cardShapeClass} card--extend-height${cardCompact}${cardRatioClass}" style="--ratio-percent: ${effPercent}%;">
      <div class="card__inner${innerGradient} ratio" style="--ratio-percent: ${effPercent}%;">
        ${mediaBlock}
        <div class="card__content">
          <div class="card__information">
            <h3 class="card__heading"${firstTitleIdAttr}>
              <a href="${escapeHtml(productUrl)}" id="StandardCardNoMediaLink-${sid}-${pid}" class="full-unstyled-link" aria-labelledby="StandardCardNoMediaLink-${sid}-${pid} NoMediaStandardBadge-${sid}-${pid}">${title}</a>
            </h3>
          </div>
          <div class="card__badge ${badgePos}">${badgeInner}</div>
        </div>
      </div>
      <div class="card__content">
        <div class="card__information">
          <h3 class="card__heading${imgUrl || cardStyle === 'standard' ? ' h5' : ''}"${secondTitleIdAttr}>
            <a href="${escapeHtml(productUrl)}" id="CardLink-${sid}-${pid}" class="full-unstyled-link" aria-labelledby="CardLink-${sid}-${pid} Badge-${sid}-${pid}">${title}</a>
          </h3>
          <div class="card-information">
            ${vendorHtml}
            ${buildPriceHtml(product, cfg)}
            ${colorBlock}
          </div>
        </div>
      </div>
      <div class="card__badge ${badgePos}">
        ${
          product.available === false
            ? `<span id="Badge-${sid}-${pid}" class="badge badge--bottom-left color-${soldScheme}">${escapeHtml(cfg.strings.soldOut)}</span>`
            : v && compareCents(v) != null && compareCents(v) > priceCents(v) && product.available !== false
              ? `<span id="Badge-${sid}-${pid}" class="badge badge--bottom-left color-${saleScheme}">${escapeHtml(cfg.strings.onSale)}</span>`
              : ''
        }
      </div>
    </div>
  </div>
</li>`;
  }

  async function fetchProductJson(rootUrl, handle) {
    const base = rootUrl.replace(/\/$/, '');
    const url = `${base}/products/${encodeURIComponent(handle)}.js`;
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('fetch');
    return res.json();
  }

  async function initSection(root) {
    const jsonEl = root.querySelector('[data-recently-viewed-config]');
    if (!jsonEl) return;
    let cfg;
    try {
      cfg = JSON.parse(jsonEl.textContent);
    } catch {
      return;
    }
    const listEl = root.querySelector('[data-recently-viewed-list]');
    const inner = root.querySelector('[data-recently-viewed-inner]');
    if (!listEl || !inner) return;

    let handles = readHandles();
    if (cfg.currentHandle) handles = handles.filter((h) => h !== cfg.currentHandle);
    handles = handles.slice(0, cfg.maxProducts);

    if (!handles.length) {
      root.setAttribute('hidden', 'hidden');
      return;
    }

    const containsStandard = cfg.cardStyle === 'standard' ? ' contains-card--standard' : '';
    listEl.className = `grid product-grid contains-card contains-card--product${containsStandard} grid--${cfg.columnsDesktop}-col-desktop grid--${cfg.columnsMobile}-col-tablet-down`;

    const fragments = [];
    for (const handle of handles) {
      try {
        const product = await fetchProductJson(cfg.rootUrl, handle);
        fragments.push(buildCardHtml(product, cfg));
      } catch {
        /* missing or unpublished */
      }
    }

    if (!fragments.length) {
      root.setAttribute('hidden', 'hidden');
      return;
    }

    listEl.innerHTML = fragments.join('');
    root.removeAttribute('hidden');
  }

  function initAll() {
    document.querySelectorAll('[data-recently-viewed-section]').forEach((root) => initSection(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root =
      (event.target && event.target.matches && event.target.matches('[data-recently-viewed-section]')
        ? event.target
        : null) ||
      (event.target && event.target.querySelector && event.target.querySelector('[data-recently-viewed-section]'));
    if (root) initSection(root);
  });
})();
